"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { MdClose, MdMenu } from "react-icons/md";

import { logEvent } from "@/services/firebase";
import { ScheduledAppointment } from "./ui";

interface INavigationLink {
  key: string;
  href: string;
  label: string;
  isButton?: boolean;
}

const navigationLinks: INavigationLink[] = [
  { key: "about", href: "#about", label: "Sobre" },
  { key: "results", href: "#testimonials", label: "Resultados" },
  { key: "specialties", href: "#specialties", label: "Especialidades" },
  { key: "how-to-work", href: "#journey", label: "Como Funciona?" },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isMenuOpen]);

  const handleSmoothScroll = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    href: string
  ) => {
    event.preventDefault();
    const targetElement = document.getElementById(href.replace("#", ""));
    if (targetElement) {
      const headerHeight = headerRef.current?.offsetHeight || 80;
      const targetPosition =
        targetElement.getBoundingClientRect().top +
        window.scrollY -
        headerHeight -
        10;
      window.scrollTo({ top: targetPosition, behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="w-full bg-white shadow-md fixed z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          <div className="flex-shrink-0">
            <Link href="/">
              <Image
                src="/images/logo-full.png"
                alt="Logotipo da empresa"
                width={260}
                height={68}
                className="hidden lg:block cursor-pointer w-52 h-14"
                priority
              />
              <Image
                src="/images/logo-mini.png"
                alt="Logotipo da empresa"
                width={40}
                height={40}
                className="block lg:hidden cursor-pointer w-10 h-10"
                priority
              />
            </Link>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            {navigationLinks.map(({ key, href, label }) => (
              <Link
                key={key}
                href={href}
                className="text-paragraph hover:opacity-95"
                onClick={e => {
                  handleSmoothScroll(e, href);
                  logEvent("navbar_click", { section: key });
                }}
              >
                {label}
              </Link>
            ))}
            <ScheduledAppointment section="header" />
          </nav>

          <div className="md:hidden flex items-center">
            <button
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-paragraph hover:opacity-95 focus:outline-none"
            >
              {isMenuOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className={`md:hidden fixed top-16 left-0 right-0 bg-white shadow-lg transition-transform duration-300 ${
            isMenuOpen ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          <nav className="flex flex-col space-y-4 px-8 py-4">
            {navigationLinks.map(({ key, href, label }) => (
              <Link
                key={key}
                href={href}
                className="text-paragraph hover:opacity-95"
                onClick={e => {
                  handleSmoothScroll(e, href);
                  logEvent("navbar_click", { section: key });
                }}
              >
                {label}
              </Link>
            ))}
            <ScheduledAppointment className="text-center" section="navbar" />
          </nav>
        </div>
      )}
    </header>
  );
};
